import { parseUTCFromAPI } from '@/composable/useFormatDateAndHours'
import { format } from 'date-fns'
import type { IEnergySensorReading } from '@/interfaces/stores/register'

// Mês de referência de fatura: Date <-> chave 'YYYY-MM'.
export function toMonthKey(date: Date): string {
  return format(date, 'yyyy-MM')
}

// Aceita 'YYYY-MM' ou ISO 'YYYY-MM-DD'; devolve o primeiro dia do mês.
export function monthKeyToDate(key: string): Date {
  const [year, month] = key.split('-').map(Number)
  return new Date(year, (month || 1) - 1, 1)
}

export const formatMinutesToHoursWithReadbleMinutes = (minutes: number) => {
  if (!Number.isFinite(minutes) || minutes === 0) return '0min'

  const abs = Math.abs(minutes)
  const days = Math.floor(abs / 1440)
  const hours = Math.floor((abs % 1440) / 60)
  const minutesLeft = Math.floor(abs % 60)
  const seconds = Math.floor((abs % 1) * 60)

  if (abs < 1) {
    return `${seconds}s`
  }

  let result = ''

  if (days > 0) {
    result += `${days}d`
  }

  if (hours > 0) {
    if (result) result += ' '
    result += `${hours}h`
  }

  if (minutesLeft > 0 || (days === 0 && hours === 0)) {
    if (result) result += ' '
    result += `${minutesLeft}min`
  }

  if (seconds > 0 && days === 0 && hours === 0) {
    result += ` ${seconds}s`
  }

  return result || '0min'
}

/** Alias semântico: API de dashboard envia tempos em minutos (ex.: 14006 → "233h 26min"). */
export const formatDashboardMinutesFromApi = formatMinutesToHoursWithReadbleMinutes

export function formatDate(date: Date | null, dateFormat: string) {
  if (!date) return ''

  return format(date, dateFormat)
}

export function formatDateWithTime(date: string | null) {
  if (!date) return 'N/A'
  return format(new Date(date), 'dd/MM/yyyy HH:mm')
}


export function getLocalDateInputValue(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}


export function getPreviousLocalDateInputValue(fromDate: Date = new Date()): string {
  const local = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
  local.setDate(local.getDate() - 1)
  return getLocalDateInputValue(local)
}

export function nowRounded(): Date {
  const date = new Date()
  date.setSeconds(0, 0)
  return date
}

export function toDatetimeLocal(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

export function fromDatetimeLocal(value: string): Date {
  if (!value) return new Date()
  return new Date(value)
}

export function buildHoursRange(hours: number, end: Date = nowRounded()): { start: Date; end: Date } {
  const start = new Date(end.getTime() - hours * 60 * 60 * 1000)
  return { start, end }
}

export function formatRangeSummary(start: Date, end: Date): string {
  const format = (date: Date) =>
    date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })

  return `${format(start)} – ${format(end)}`
}

export function formatRangeSummaryShort(start: Date, end: Date): string {
  const format = (date: Date) =>
    date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

  return `${format(start)} - ${format(end)}`
}

export interface ILocalCalendarDayPeriod {
  dayStartMs: number
  dayEndMs: number
  startDate: string
  endDate: string
}

/**
 * Dia civil no fuso do navegador: limites em ms locais + intervalo UTC com Z para a API.
 * Fonte única para Gantt, query de performance e validação de data selecionável.
 */
export function getLocalCalendarDayPeriod(dateInput: string): ILocalCalendarDayPeriod {
  const [year, month, day] = dateInput.split('-').map(Number)
  const dayStart = new Date(year, month - 1, day, 0, 0, 0, 0)
  const nextDayStart = new Date(year, month - 1, day + 1, 0, 0, 0, 0)
  const dayEnd = new Date(nextDayStart.getTime() - 1)

  const { startDate, endDate } = getAnalysisPeriodRange([dayStart, dayEnd])

  return {
    dayStartMs: dayStart.getTime(),
    dayEndMs: dayEnd.getTime(),
    startDate,
    endDate,
  }
}

export function getLocalCalendarDayBounds(dateInput: string): Pick<ILocalCalendarDayPeriod, 'dayStartMs' | 'dayEndMs'> {
  const { dayStartMs, dayEndMs } = getLocalCalendarDayPeriod(dateInput)
  return { dayStartMs, dayEndMs }
}

export function getLocalCalendarDayUtcRange(dateInput: string): Pick<ILocalCalendarDayPeriod, 'startDate' | 'endDate'> {
  const { startDate, endDate } = getLocalCalendarDayPeriod(dateInput)
  return { startDate, endDate }
}

/** Dia civil local válido para performance: o dia de análise precisa ter terminado no fuso do navegador. */
export function isLocalCalendarDaySelectableForPerformance(
  dateInput: string,
  now: Date = new Date(),
): boolean {
  const { dayEndMs } = getLocalCalendarDayPeriod(dateInput)
  return dayEndMs <= now.getTime()
}

/** Último dia civil local selecionável (dia completo no fuso do navegador). */
export function getLatestSelectableLocalDateInputValue(fromDate: Date = new Date()): string {
  const candidate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())

  for (let i = 0; i < 366; i++) {
    const dateInput = getLocalDateInputValue(candidate)
    if (isLocalCalendarDaySelectableForPerformance(dateInput, fromDate)) {
      return dateInput
    }
    candidate.setDate(candidate.getDate() - 1)
  }

  return getLocalDateInputValue(candidate)
}

/** Instante ISO UTC da API → data/hora no fuso do navegador. */
export function formatApiDateTimeLocal(apiValue: string | null | undefined): string {
  if (!apiValue) return 'N/A'
  const date = parseUTCFromAPI(apiValue)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return format(date, 'dd/MM/yyyy HH:mm')
}

/** Instante ISO UTC da API → HH:mm no fuso do navegador. */
export function formatApiTimeLocal(apiValue: string | null | undefined): string {
  if (!apiValue) return ''
  const date = parseUTCFromAPI(apiValue)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

export function formatWithLocalTimezone(timestamp: number) {
  const isSeconds = timestamp.toString().length === 10
  const dateInMs = isSeconds ? timestamp * 1000 : timestamp
  const localDate = new Date(dateInMs)
  const localDateWithOffset = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000)

  return format(localDateWithOffset, 'dd/MM/yyyy HH:mm')
}

/**
 * Converte o domingo da semana de análise (emitido pelo WeekSelector) no intervalo
 * local [segunda 00:00:00.000, domingo 23:59:59.999], alinhado ao período usado na API de anomaly_detectors.
 */
export function periodFromAnomalyWeekSunday(weekSunday: Date | null): [Date, Date] | null {
  if (!weekSunday) return null
  const end = new Date(weekSunday)
  if (Number.isNaN(end.getTime())) return null
  end.setHours(0, 0, 0, 0)
  const start = new Date(end)
  start.setDate(start.getDate() - 7)
  start.setHours(0, 0, 0, 0)
  return [start, end]
}

export function formatPeriodDate(period: Date[]) {
  const start = period[0]
  const end = period[1]

  const startUTC = new Date(start.getTime() + start.getTimezoneOffset() * 60000)
  const endUTC = new Date(end.getTime() + end.getTimezoneOffset() * 60000)

  return `start_date=${format(startUTC, 'yyyy-MM-dd')}T${format(startUTC, 'HH:mm:ss')}Z&end_date=${format(endUTC, 'yyyy-MM-dd')}T${format(endUTC, 'HH:mm:ss')}Z`
}

/** Mesmo intervalo de `formatPeriodDate` (ex.: gráfico de registros): query string + datas para outras APIs. */
export function getAnalysisPeriodRange(period: Date[]): {
  dateRange: string
  startDate: string
  endDate: string
} {
  const dateRange = formatPeriodDate(period)
  const sp = new URLSearchParams(dateRange)
  return {
    dateRange,
    startDate: sp.get('start_date') ?? '',
    endDate: sp.get('end_date') ?? '',
  }
}

/** Verifica se a leitura em cache corresponde ao mesmo `dateRange` de `formatPeriodDate` / readings. */
export function energyReadingMatchesDateRange(reading: IEnergySensorReading, dateRange: string): boolean {
  const sp = new URLSearchParams(dateRange)
  const reqStart = sp.get('start_date')
  const reqEnd = sp.get('end_date')
  if (!reqStart || !reqEnd) return false
  const t = (s: string) => new Date(s).getTime()
  return t(reading.start_date) === t(reqStart) && t(reading.end_date) === t(reqEnd)
}

/** Data âncora só para aritmética de horário, já após o fim do horário de verão no Brasil. */
const TIME_OF_DAY_ANCHOR_YEAR = 2024
const TIME_OF_DAY_ANCHOR_MONTH_INDEX = 5
const TIME_OF_DAY_ANCHOR_DAY = 15
const TIME_OF_DAY_ANCHOR_UTC_MS = Date.UTC(
  TIME_OF_DAY_ANCHOR_YEAR,
  TIME_OF_DAY_ANCHOR_MONTH_INDEX,
  TIME_OF_DAY_ANCHOR_DAY,
  0,
  0,
  0,
)

const TIME_OF_DAY_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function createLocalTimeOfDayAnchor(h: number, m: number, s: number): Date {
  return new Date(
    TIME_OF_DAY_ANCHOR_YEAR,
    TIME_OF_DAY_ANCHOR_MONTH_INDEX,
    TIME_OF_DAY_ANCHOR_DAY,
    h,
    m,
    s,
  )
}

function parseUtcTimeOfDayParts(value: string | Date): { h: number; m: number; s: number } | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return { h: value.getUTCHours(), m: value.getUTCMinutes(), s: value.getUTCSeconds() }
  }
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.includes('T')) {
    const d = new Date(trimmed)
    if (Number.isNaN(d.getTime())) return null
    return { h: d.getUTCHours(), m: d.getUTCMinutes(), s: d.getUTCSeconds() }
  }
  const m = trimmed.match(TIME_OF_DAY_RE)
  if (!m) return null
  return { h: Number(m[1]), m: Number(m[2]), s: m[3] !== undefined ? Number(m[3]) : 0 }
}

/**
 * Horário do dia vindo da API como relógio em UTC (ex.: perfis "fora do horário regular", "horário de pico").
 * Converte para HH:mm no fuso local do navegador (edição / exibição).
 */
export function formatOutsideRegularUtcTimeToLocalHHmm(apiValue: string | Date | null | undefined): string {
  if (apiValue == null || apiValue === '') return ''
  const parts = parseUtcTimeOfDayParts(typeof apiValue === 'string' ? apiValue : apiValue)
  if (!parts) return ''
  const utcMs = TIME_OF_DAY_ANCHOR_UTC_MS + (parts.h * 3600 + parts.m * 60 + parts.s) * 1000
  return format(new Date(utcMs), 'HH:mm')
}

/**
 * Valor do input type="time" no horário local → HH:mm:ss em UTC para persistir na API.
 */
export function localTimeInputToUtcHHmmssForApi(localTime: string): string {
  const trimmed = localTime.trim()
  const m = trimmed.match(TIME_OF_DAY_RE)
  if (!m) return trimmed
  const lh = Number(m[1])
  const lm = Number(m[2])
  const ls = m[3] !== undefined ? Number(m[3]) : 0
  const local = createLocalTimeOfDayAnchor(lh, lm, ls)
  return `${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}:${pad2(local.getUTCSeconds())}`
}

/**
 * Input local (HH:mm ou HH:mm:ss) → instante ISO em 2000-01-01 UTC, como o perfil de horário de pico envia à API.
 */
export function localTimeInputToUtcIsoInstantForApi(localTime: string): string {
  const utcHms = localTimeInputToUtcHHmmssForApi(localTime)
  const segs = utcHms.split(':').map((x) => Number(x))
  if (segs.length < 2 || segs.some((n) => Number.isNaN(n))) {
    return new Date(localTime).toISOString()
  }
  const [h, m, s = 0] = segs
  return new Date(Date.UTC(2000, 0, 1, h, m, s)).toISOString()
}
